import React, { useState, useEffect } from 'react';
import { Search, ShieldAlert, Monitor, Terminal, MapPin, Eye, RefreshCw, ThumbsUp } from 'lucide-react';
import axios from 'axios';
import constant from '../constants/constant';
import { CountryBadge } from '../components/SearchableCountrySelect';

const SecurityLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchLogs();
  }, []);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${constant.API_BASE_URL}/auth/login-history`, {
        headers: { Authorization: sessionStorage.getItem('adminToken') }
      });
      if (response.data.success) {
        setLogs(response.data.data);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const filteredLogs = logs.filter(log => 
    log.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (log.ip_address && log.ip_address.includes(searchTerm)) ||
    (log.status && log.status.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const formatDeviceName = (log) => {
    const candidates = [log.device_name, log.device_info, log.user_agent];
    const valid = candidates.find(c => c && typeof c === 'string' && !c.includes('Unknown User-Agent') && c.trim() !== '');
    return valid || 'Unknown Device';
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-3xl font-extrabold text-slate-800">Security & Login Audit Logs</h2>
          <p className="text-slate-500 mt-1">Monitor admin portal login attempts, verify device details, and trace IP addresses.</p>
        </div>
        <button 
          onClick={fetchLogs}
          className="flex items-center gap-2 px-5 py-2.5 bg-white border border-slate-200 text-slate-700 font-semibold rounded-xl hover:bg-slate-50 transition active:scale-95 shadow-sm"
        >
          <RefreshCw size={18} className={loading ? "animate-spin" : ""} /> Reload Audit Logs <ThumbsUp size={18} className="text-pink-500 ml-1" />
        </button>
      </div>

      <div className="bg-white rounded-[24px] shadow-xl shadow-slate-200/50 border border-slate-100 overflow-hidden">
        {/* Search */}
        <div className="p-6 border-b border-slate-100 flex items-center justify-between gap-4">
          <div className="relative w-full sm:w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              placeholder="Search by Email or IP..."
              className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-pink-500/20 transition"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="text-sm font-bold text-slate-500 bg-slate-50 px-4 py-2 rounded-lg border border-slate-100">
            Total Records: <span className="text-pink-600">{filteredLogs.length}</span>
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="p-20 text-center">
            <div className="animate-spin w-10 h-10 border-4 border-pink-500 border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-slate-500">Retrieving audit trail...</p>
          </div>
        ) : filteredLogs.length === 0 ? (
          <div className="p-20 text-center text-slate-400">
            <ShieldAlert size={48} className="mx-auto mb-4 opacity-30" />
            <p>No audit logs matching search term found.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50/50 text-slate-400 text-xs font-bold uppercase tracking-wider">
                  <th className="p-6">User Email</th>
                  <th className="p-6">Location</th>
                  <th className="p-6">IP Address</th>
                  <th className="p-6">Device & OS</th>
                  <th className="p-6">Login Time</th>
                  <th className="p-6">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-sm">
                {filteredLogs.map((log) => (
                  <tr key={log.id} className="hover:bg-slate-50/50 transition">
                    <td className="p-6 font-bold text-slate-800">{log.email}</td>
                    <td className="p-6">
                      <CountryBadge country={log.country} />
                    </td>
                    <td className="p-6 text-slate-600 font-mono">
                      <span className="flex items-center gap-1.5">
                        <MapPin size={14} className="text-slate-400" />
                        {log.ip_address || "127.0.0.1"}
                      </span>
                    </td>
                    <td className="p-6">
                      <div className="flex items-center gap-2">
                        <Monitor size={16} className="text-slate-400" />
                        <div>
                          <p className="font-semibold text-slate-700">{log.os || 'Unknown OS'} - {log.browser || 'Unknown Browser'}</p>
                          <p className="text-[10px] text-slate-400 truncate max-w-xs" title={log.device_name || log.user_agent || log.device_info}>
                            {formatDeviceName(log)}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="p-6 text-slate-500">{new Date(log.login_time).toLocaleString()}</td>
                    <td className="p-6">
                      <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide ${
                        log.status === 'SUCCESS' ? 'bg-green-100 text-green-700' :
                        log.status === 'PENDING' ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'
                      }`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${
                          log.status === 'SUCCESS' ? 'bg-green-500' :
                          log.status === 'PENDING' ? 'bg-yellow-500' : 'bg-red-500'
                        }`}></span>
                        {log.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default SecurityLogs;
